import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const COOKIE_KEY = 'onetv_cookie_consent';

type ConsentState = 'accepted' | 'declined' | null;

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY) as ConsentState;
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = (choice: 'accepted' | 'declined') => {
    setAnimateOut(true);
    setTimeout(() => {
      localStorage.setItem(COOKIE_KEY, choice);
      setVisible(false);
      setAnimateOut(false);
    }, 350);
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4 sm:pb-6 transition-all duration-350 ${
        animateOut ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      style={{ transition: 'opacity 0.35s ease, transform 0.35s ease' }}
    >
      <div className="max-w-4xl mx-auto bg-gray-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Main bar */}
        <div className="p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Icon */}
          <div className="w-10 h-10 flex items-center justify-center bg-amber-500/15 rounded-xl flex-shrink-0">
            <i className="ri-shield-check-line text-amber-400 text-xl" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold mb-0.5">We use cookies</p>
            <p className="text-gray-400 text-xs leading-relaxed">
              We use cookies to improve your experience, analyse traffic, and serve personalised content.
              By clicking &quot;Accept All&quot;, you consent to our use of cookies.{' '}
              <button
                onClick={() => setShowDetails(v => !v)}
                className="text-[#7dd3fc] hover:underline cursor-pointer text-xs"
              >
                {showDetails ? 'Hide details' : 'Learn more'}
              </button>
              {' '}or read our{' '}
              <Link to="/privacy" className="text-[#7dd3fc] hover:underline cursor-pointer text-xs">
                Privacy Policy
              </Link>.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
            <button
              onClick={() => dismiss('declined')}
              className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-white/15 text-gray-400 hover:text-white hover:border-white/30 text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              Decline
            </button>
            <button
              onClick={() => dismiss('accepted')}
              className="flex-1 sm:flex-none px-5 py-2 rounded-lg bg-gradient-to-r from-[#0284c7] to-[#0ea5e9] hover:from-[#0369a1] hover:to-[#0284c7] text-white text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
            >
              Accept All
            </button>
          </div>
        </div>

        {/* Expandable details */}
        {showDetails && (
          <div className="border-t border-white/8 px-4 sm:px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              {
                icon: 'ri-settings-3-line',
                title: 'Essential Cookies',
                desc: 'Required for the site to function. Cannot be disabled.',
                always: true,
              },
              {
                icon: 'ri-bar-chart-line',
                title: 'Analytics Cookies',
                desc: 'Help us understand how visitors interact with our site.',
                always: false,
              },
              {
                icon: 'ri-advertisement-line',
                title: 'Marketing Cookies',
                desc: 'Used to deliver relevant ads and track campaign performance.',
                always: false,
              },
            ].map(item => (
              <div key={item.title} className="flex items-start gap-3 bg-white/4 rounded-xl p-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/8 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-gray-300 text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="text-white text-xs font-semibold">{item.title}</p>
                    {item.always ? (
                      <span className="text-[10px] text-emerald-400 font-medium whitespace-nowrap">Always on</span>
                    ) : (
                      <span className="text-[10px] text-gray-500 whitespace-nowrap">Optional</span>
                    )}
                  </div>
                  <p className="text-gray-500 text-[11px] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
